import React, { useEffect, useCallback, useState, useRef } from "react";
import axios from "../../api";
import {
  Card,
  Button,
  TextField,
  FormControl,
  Grid,
  Box,
  Autocomplete,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { AxiosError } from "axios";

const todayDate = new Date().toISOString().split("T")[0];

const validationSchema = Yup.object({
  customer_id: Yup.string().required("User is required"),
  asset_id: Yup.string().required("Asset is required"),
  asset_type_id: Yup.string().required("Asset type is required"),
  subscription_type_id: Yup.string().required("Subscription type is required"),
  start_date: Yup.string()
    .matches(
      /^\d{4}-\d{2}-\d{2}$/,
      "Start date must be in the format YYYY-MM-DD"
    )
    .required("Start date is required")
    .test("is-valid-date", "Start date is not a valid date", (value) => {
      return !Number.isNaN(new Date(value).getTime());
    }),
  end_date: Yup.string()
    .matches(/^\d{4}-\d{2}-\d{2}$/, "End date must be in the format YYYY-MM-DD")
    .required("End date is required")
    .test("is-valid-date", "End date is not a valid date", (value) => {
      return !Number.isNaN(new Date(value).getTime());
    })
    .test(
      "is-greater-than-today",
      "End date must be greater than today",
      (value) => {
        if (!value) return true;
        const endDate = new Date(value);
        return endDate > new Date(todayDate);
      }
    ),
});

interface AssetType {
  asset_type_id: number;
  asset_type_name: string;
}

interface Asset {
  asset_id: number;
  asset_name: string;
}

interface Customers {
  customer_id: number;
  name: string;
  type: string;
}

interface Subscription {
  subscription_type_id: number;
  subscription_type_name: string;
  duration_in_days: number;
}

interface Division {
  division_id: number;
  division_name: string;
}

export interface AddSubscriptionProps {
  embedded?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
  /** When set (e.g. from customer Subscriptions overlay), customer is pre-filled; if only one asset, asset type and asset are also pre-filled */
  initialCustomerId?: number;
}

const AddSubscription = ({
  embedded,
  onSuccess,
  onCancel,
  initialCustomerId,
}: AddSubscriptionProps = {}) => {
  const navigate = useNavigate();
  const { fetchError, fetchSuccess } = useSnackbar();
  const [users, setUsers] = useState<Customers[]>([]);
  const [assetTypesData, setAssetTypesData] = useState<AssetType[]>([]);
  const [subscriptionTypes, setSubscriptionTypes] = useState<Subscription[]>(
    []
  );
  const [selectedSubscriptionType, setSelectedSubscriptionType] =
    useState<Subscription | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [divisionAssets, setDivisionAssets] = useState<Asset[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(initialCustomerId ?? null);
  const [selectedCustomerType, setSelectedCustomerType] = useState<
    string | null
  >(null);

  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  const [selectedAssetType, setSelectedAssetType] = useState<AssetType | null>(
    null
  );
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [selectedDivisions, setSelectedDivisions] = useState<Division | null>(
    null
  );

  // State for managing start and end dates
  const [startDateValue, setStartDateValue] = useState<string>("");
  const [calculatedEndDate, setCalculatedEndDate] = useState<string>("");
  const hasAutoFilledAsset = useRef(false);

  useEffect(() => {
    if (initialCustomerId && users.length) {
      const user = users.find((u) => u.customer_id === initialCustomerId);
      if (user) {
        setSelectedCustomer(initialCustomerId);
        setSelectedCustomerType(user.type ?? "customer");
      }
    }
  }, [initialCustomerId, users]);

  const fetchUsersData = async () => {
    try {
      const response = await axios.get("/customer");
      setUsers(response?.data?.list || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    fetchUsersData();
  }, []);

  const fetchAssetsData = useCallback(async () => {
    try {
      const assetTypesData = await axios.get("/asset-types", {
        params: { is_active: 1 },
      });
      setAssetTypesData(assetTypesData?.data?.list || []);
    } catch (error) {
      console.error("Error fetching asset types:", error);
    }
  }, []);

  useEffect(() => {
    fetchAssetsData();
  }, [fetchAssetsData]);

  const fetchAssets = useCallback(async () => {
    if (selectedCustomer && selectedAssetType?.asset_type_id) {
      try {
        const response = await axios.get(
          `/assets/${selectedAssetType?.asset_type_id}/${selectedCustomer}`
        );
        setAssets(response?.data?.list || []);
      } catch (error) {
        console.error("Error fetching assets:", error);
      }
    }
  }, [selectedCustomer, selectedAssetType?.asset_type_id]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const fetchAssetsByDivisionsAndAssetTypes = useCallback(async () => {
    if (
      selectedCustomer &&
      selectedAssetType?.asset_type_id &&
      selectedDivisions?.division_id
    ) {
      try {
        const response = await axios.get(
          `/assets/${selectedDivisions?.division_id}/${selectedAssetType?.asset_type_id}/${selectedCustomer}`
        );
        setDivisionAssets(response?.data?.list || []);
      } catch (error) {
        console.error("Error fetching assets:", error);
      }
    }
  }, [
    selectedCustomer,
    selectedAssetType?.asset_type_id,
    selectedDivisions?.division_id,
  ]);

  useEffect(() => {
    if (!selectedCustomer || !selectedAssetType?.asset_type_id) return;

    if (selectedCustomerType === "customer") {
      fetchAssets();
    } else if (
      selectedCustomerType === "company" &&
      selectedDivisions?.division_id
    ) {
      fetchAssetsByDivisionsAndAssetTypes();
    }
  }, [
    selectedCustomer,
    selectedAssetType?.asset_type_id,
    selectedCustomerType,
    selectedDivisions?.division_id,
    fetchAssets,
    fetchAssetsByDivisionsAndAssetTypes,
  ]);

  const fetchSubsciptionTypes = useCallback(async () => {
    try {
      const subscriptionTypes = await axios.get(
        `/subscription-types/assets/${selectedAssetType?.asset_type_id}`,
        {
          params: { is_active: 1 },
        }
      );
      setSubscriptionTypes(subscriptionTypes?.data?.list || []);
    } catch (error) {
      console.error("Error fetching asset types:", error);
    }
  }, [selectedAssetType?.asset_type_id]);

  useEffect(() => {
    fetchSubsciptionTypes();
  }, [fetchSubsciptionTypes]);

  const today = new Date();
  const formattedToday = today.toISOString().split("T")[0];

  const fetchDivisions = useCallback(async () => {
    try {
      const response = await axios.get(
        `/divisions/customer/${selectedCustomer}`
      );
      setDivisions(response?.data?.divisions);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  }, [selectedCustomer]);

  useEffect(() => {
    fetchDivisions();
  }, [fetchDivisions]);

  const assetOptions =
    selectedCustomerType === "company" ? divisionAssets : assets;

  // Calculate end date when subscription type or start date changes
  useEffect(() => {
    if (selectedSubscriptionType && startDateValue) {
      const durationDays = selectedSubscriptionType?.duration_in_days || 0;
      if (durationDays > 0) {
        const start = new Date(startDateValue);
        const end = new Date(start);
        end.setDate(start.getDate() + durationDays);
        const endDate = end.toISOString().split("T")[0];
        setCalculatedEndDate(endDate);
      }
    } else {
      setCalculatedEndDate("");
    }
  }, [selectedSubscriptionType, startDateValue]);

  const content = (
    <Box sx={{ width: "100%", ...(!embedded && { p: 3, borderRadius: 2, boxShadow: 2 }) }}>
      {!embedded && (
        <SoftTypography variant="h5" style={{ marginBottom: "18px" }}>
          Add New Subscription
        </SoftTypography>
      )}
      <Formik
          enableReinitialize
          key={initialCustomerId ?? "sub-default"}
          initialValues={{
            customer_id: initialCustomerId ? String(initialCustomerId) : "",
            subscription_type_id: null,
            start_date: "",
            end_date: "",
            asset_id: "",
            division_id: null,
            asset_type_id: "",
            lift_id: null,
          }}
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            values.division_id ??= null;
            try {
              const { asset_type_id, ...rest } = values;
              const response = await axios.post("/subscription", rest);
              fetchSuccess(response.data.message);
              if (embedded && onSuccess) {
                onSuccess();
              } else {
                navigate("/subscriptions-list");
              }
            } catch (error) {
              if (error instanceof AxiosError && error.response) {
                fetchError(error.response.data?.error);
              }
            }
          }}
        >
          {({ errors, touched, setFieldValue, values }) => {
            // Sync calculated end date to form when it changes
            if (calculatedEndDate && values.end_date !== calculatedEndDate) {
              setFieldValue("end_date", calculatedEndDate);
            }
            // Auto-fill single asset when initialCustomerId and only one asset type and one asset (once)
            const assetOpts = selectedCustomerType === "company" ? divisionAssets : assets;
            if (
              initialCustomerId &&
              selectedCustomer &&
              assetTypesData.length === 1 &&
              assetOpts.length === 1 &&
              !values.asset_id &&
              !hasAutoFilledAsset.current
            ) {
              hasAutoFilledAsset.current = true;
              const at = assetTypesData[0];
              const ast = assetOpts[0];
              setFieldValue("asset_type_id", at?.asset_type_id ?? "");
              setFieldValue("asset_id", ast?.asset_id ?? "");
              setSelectedAssetType(at);
              setSelectedAsset(ast);
            }

            return (
              <Form
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Customer
                      </SoftTypography>
                      <Autocomplete
                        options={users || []}
                        getOptionLabel={(option) => `${option.name}`}
                        value={users?.find((u) => u.customer_id === Number(values.customer_id)) ?? null}
                        onChange={(event, value) => {
                          setFieldValue(
                            "customer_id",
                            value?.customer_id || ""
                          );
                          setSelectedCustomer(value?.customer_id || null);
                          setSelectedCustomerType(value?.type || null);
                          setFieldValue("asset_type_id", "");
                          setFieldValue("asset_id", "");
                          setFieldValue("subscription_type_id", "");
                          setSelectedAsset(null);
                          setSelectedSubscriptionType(null);
                          setSelectedAssetType(null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={!!errors.customer_id && touched.customer_id}
                            helperText={
                              touched.customer_id && errors.customer_id
                            }
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  {selectedCustomerType === "company" && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <SoftTypography variant="caption" fontWeight="bold">
                          Division
                        </SoftTypography>
                        <Autocomplete
                          options={divisions || []}
                          getOptionLabel={(option) => `${option.division_name}`}
                          value={selectedDivisions || null}
                          onChange={(event, value) => {
                            setFieldValue(
                              "division_id",
                              value ? value.division_id : null
                            );
                            setSelectedDivisions(value);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              error={
                                !!errors.division_id && touched.division_id
                              }
                              helperText={
                                touched.division_id && errors.division_id
                              }
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Asset Type
                      </SoftTypography>
                      <Autocomplete
                        options={assetTypesData || []}
                        getOptionLabel={(option) => option.asset_type_name}
                        value={selectedAssetType}
                        onChange={(event, value) => {
                          setFieldValue(
                            "asset_type_id",
                            value?.asset_type_id || ""
                          );
                          setSelectedAssetType(value);
                          setFieldValue("asset_id", "");
                          setSelectedAsset(null);
                          setFieldValue("subscription_type_id", "");
                          setSelectedSubscriptionType(null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={
                              !!errors.asset_type_id && touched.asset_type_id
                            }
                            helperText={
                              touched.asset_type_id && errors.asset_type_id
                            }
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Asset
                      </SoftTypography>
                      <Autocomplete
                        options={assetOptions || []}
                        getOptionLabel={(option) => option.asset_name}
                        value={selectedAsset}
                        onChange={(event, value) => {
                          setFieldValue("asset_id", value?.asset_id || "");
                          setSelectedAsset(value);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={!!errors.asset_id && touched.asset_id}
                            helperText={touched.asset_id && errors.asset_id}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Subscription Type
                      </SoftTypography>
                      <Autocomplete
                        options={subscriptionTypes || []}
                        getOptionLabel={(option) =>
                          option?.subscription_type_name
                        }
                        value={selectedSubscriptionType}
                        onChange={(event, value) => {
                          setFieldValue(
                            "subscription_type_id",
                            value?.subscription_type_id || ""
                          );
                          setSelectedSubscriptionType(value);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={
                              !!errors.subscription_type_id &&
                              touched.subscription_type_id
                            }
                            helperText={
                              touched.subscription_type_id &&
                              errors.subscription_type_id
                            }
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Start Date
                      </SoftTypography>
                      <Field
                        className="date-field"
                        name="start_date"
                        as={TextField}
                        type="date"
                        fullWidth
                        InputProps={{
                          inputProps: { min: formattedToday },
                        }}
                        variant="outlined"
                        error={errors.start_date && touched.start_date}
                        helperText={touched.start_date && errors.start_date}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const value = e.target.value;
                          setFieldValue("start_date", value);
                          setStartDateValue(value);
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <SoftTypography variant="caption" fontWeight="bold">
                        End Date
                      </SoftTypography>
                      <Field
                        className="date-field"
                        name="end_date"
                        as={TextField}
                        type="date"
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          readOnly: true,
                        }}
                        error={errors.end_date && touched.end_date}
                        helperText={touched.end_date && errors.end_date}
                      />
                    </FormControl>
                  </Grid>
                </Grid>

                <Grid item xs={12} md={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      className="success-btn"
                      sx={{
                        position: "relative",
                        minWidth: 100,
                      }}
                      variant="contained"
                      type="submit"
                      color="success"
                    >
                      Save
                    </Button>

                    <Button
                      className="cancel-btn"
                      sx={{
                        position: "relative",
                        minWidth: 100,
                        ml: 2.5,
                      }}
                      variant="contained"
                      color="warning"
                      onClick={() =>
                        embedded && onCancel
                          ? onCancel()
                          : navigate("/subscriptions-list")
                      }
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Form>
            );
          }}
        </Formik>
    </Box>
  );

  if (embedded) {
    return content;
  }
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {content}
    </DashboardLayout>
  );
};

export default AddSubscription;
